import type { Functions } from 'firebase/functions'
import { httpsCallable } from 'firebase/functions'
import type { EntitlementsStorageModule } from '../storage'
import type { CacheService } from '~/services/cache/cache'
import type { ErrorTrackerServiceInterface } from '~/features/error-tracker/services/types'
import type { UserFrontend } from '~common/google-auth/storage/types'
import type { GlobalEvent } from '~/types/global-events'
import type { ProductBase } from '~common/storage/payment-processing'
import type { EntitlementFrontend } from '~common/google-auth/storage/types'
import { FirebaseAuthService } from '~common/google-auth/services/firebase-auth'

export interface EntitlementsServiceDeps {
    storageModules: {
        auth: {
            getCurrentUser(): Promise<UserFrontend | null>
        }
        entitlements: EntitlementsStorageModule
    }
    functions: Functions
    getServices: () => {
        errorTracker: ErrorTrackerServiceInterface
        cache: CacheService
        globalEvents: {
            emit: (event: GlobalEvent) => void
        }
        auth: FirebaseAuthService
    }
}

export class EntitlementsService {
    constructor(private readonly deps: EntitlementsServiceDeps) {}

    private mutex = Promise.resolve()

    async getEntitlement(userId: string): Promise<EntitlementFrontend | null> {
        const cache = this.deps.getServices().cache
        let entitlement = cache.getKey('entitlement')
        if (entitlement) {
            return entitlement
        }
        entitlement =
            await this.deps.storageModules.entitlements.getEntitlement(userId)
        if (entitlement) {
            cache.setKey('entitlement', entitlement)
        } else {
            cache.setKey('entitlement', null)
        }
        return entitlement
    }

    async checkAndOrUpdateEntitlement(params?: {
        update?: boolean
        dontNotify?: boolean
        newInvoices?: number
    }): Promise<{ allowed: boolean; remainingCredits: number }> {
        const previous = this.mutex
        const current = previous.then(async () => {
            const services = this.deps.getServices()
            const currentUser =
                await this.deps.storageModules.auth.getCurrentUser()
            if (!currentUser) {
                return services.errorTracker.throwErrorWithNotification(
                    "You're not logged in",
                )
            }
            const currentEntitlement = services.cache.getKey('entitlement')
            if (!currentEntitlement) {
                return services.errorTracker.throwErrorWithNotification(
                    'Entitlement not found',
                )
            }

            if (
                currentEntitlement.invoicesRemainingSubscription +
                    currentEntitlement.invoicesRemainingConsumable <=
                0
            ) {
                if (!params?.dontNotify) {
                    services.globalEvents.emit({
                        type: 'openModal',
                        modalType: 'pricing',
                        metadata: {
                            offerId: 'no-credits',
                        },
                    })
                }
                return { allowed: false, remainingCredits: 0 }
            } else if (!params?.update) {
                return {
                    allowed: true,
                    remainingCredits:
                        currentEntitlement.invoicesRemainingSubscription +
                        currentEntitlement.invoicesRemainingConsumable,
                }
            }
            const numberToSpend = params?.newInvoices ?? 1

            if (currentEntitlement.invoicesRemainingConsumable > 0) {
                currentEntitlement.invoicesRemainingConsumable -= numberToSpend
            } else if (currentEntitlement.invoicesRemainingSubscription > 0) {
                currentEntitlement.invoicesRemainingSubscription -=
                    numberToSpend
            }

            currentEntitlement.invoiceUsage += numberToSpend
            services.cache.setKey('entitlement', currentEntitlement)

            const spendInvoiceFn = httpsCallable<{}, { success: boolean }>(
                this.deps.functions,
                'spendInvoice',
            )

            spendInvoiceFn()

            const remainingCredits =
                currentEntitlement.invoicesRemainingSubscription +
                currentEntitlement.invoicesRemainingConsumable

            return { allowed: true, remainingCredits }
        })
        this.mutex = current.then(() => {}).catch(() => {})
        return current
    }

    async getRemainingInvoiceCredits(): Promise<number> {
        const currentEntitlement = this.deps
            .getServices()
            .cache.getKey('entitlement')

        const remaining =
            (currentEntitlement?.invoicesRemainingSubscription ?? 0) +
            (currentEntitlement?.invoicesRemainingConsumable ?? 0)
        return remaining
    }

    async getSubscriptionProduct(
        forceFetch?: boolean,
    ): Promise<ProductBase | null> {
        if (!forceFetch) {
            const cache = this.deps.getServices().cache
            const offeredPricing = cache.getKey('offeredPricing')
            if (offeredPricing && !forceFetch) {
                return offeredPricing
            }
        }
        const currentUser = await this.deps.getServices().auth.getLoggedInUser()
        if (!currentUser) {
            return null
        }
        const entitlement = await this.getEntitlement(currentUser.uid)
        if (!entitlement) {
            return null
        }
        if (!entitlement?.offeredPricing) {
            return null
        }
        const getSubscriptionProductFn = httpsCallable<
            { planId: string },
            { product: ProductBase | null }
        >(this.deps.functions, 'getSubscriptionProduct')

        const result = await getSubscriptionProductFn({
            planId: entitlement.offeredPricing,
        })
        const product = result.data.product

        if (product) {
            const cache = this.deps.getServices().cache
            cache.setKey('offeredPricing', product)
        }

        return product
    }

    async createCheckout(
        variantId: number,
        userEmail: string,
    ): Promise<string> {
        const createCheckoutSessionFn = httpsCallable<
            { variantId: number; userEmail: string },
            { checkoutUrl: string }
        >(this.deps.functions, 'createCheckoutSession')

        const result = await createCheckoutSessionFn({ variantId, userEmail })
        return result.data.checkoutUrl
    }

    async getLemonSqueezyStatus(email: string): Promise<{
        lemonsqueezyUserId: number | null
        invoicesRemainingConsumable: number
    }> {
        const getStatus = httpsCallable<
            { email: string },
            {
                lemonsqueezyUserId: number | null
                invoicesRemainingConsumable: number
            }
        >(this.deps.functions, 'getLemonSqueezyStatus')

        const result = await getStatus({ email })
        return result.data
    }
}
